import { createActionGroup, emptyProps, props } from '@ngrx/store';

import {
  AtcOverviewDataStoreModel,
  CapacityGraphDataStoreModel,
  CapacityOverviewDataStoreModel,
  CreatePlannedOutageRequestStoreModel,
  CreateUnplannedOutageRequestStoreModel,
  GetAtcResponseStoreModel,
  GetNtcOptionsResponseStoreModel,
  GetNtcResponseStoreModel,
  NtcOverviewDataStoreModel,
  PlannedOutageDetailsStoreModel,
  PlannedOutageOptionsStoreModel,
  PlannedOutageStoreModel,
  UnplannedOutageDetailsStoreModel,
  UnplannedOutageOptionsStoreModel,
  UnplannedOutageStoreModel,
  UpdatePlannedOutageRequestStoreModel,
  UpdateUnplannedOutageRequestStoreModel,
} from '@domain/capacity';

import {
  CapacityGetAtcOverviewStoreModel,
  CapacityGetAtcRequestStoreModel,
  CapacityGetCapacityOverviewStoreModel,
  CapacityGetNtcGraphStoreModel,
  CapacityGetNtcOverviewStoreModel,
  CapacityGetNtcRequestStoreModel,
  CapacityNtcGetPlannedOutagesStoreModel,
  CapacityNtcGetUnplannedOutagesStoreModel,
  CapacitySubmitAtcRequestStoreModel,
  CapacitySubmitNtcRequestStoreModel,
} from './models';

export const CapacityActions = createActionGroup({
  source: 'CAPACITY',
  events: {
    'Get capacity overview': props<{ request: CapacityGetCapacityOverviewStoreModel }>(),
    'Get capacity overview success': props<{ overview: CapacityOverviewDataStoreModel }>(),
    'Get capacity overview fail': props<{ errorMsg: string }>(),

    'Get ntc overview': props<{ request: CapacityGetNtcOverviewStoreModel }>(),
    'Get ntc overview success': props<{ overview: NtcOverviewDataStoreModel }>(),
    'Get ntc overview fail': props<{ errorMsg: string }>(),

    'Refresh ntc overview': emptyProps(),
    'No last ntc overview request data found': emptyProps(),

    'Get capacity graph': props<{ request: CapacityGetNtcGraphStoreModel }>(),
    'Get capacity graph success': props<{ capacityGraph: CapacityGraphDataStoreModel }>(),
    'Get capacity graph fail': props<{ errorMsg: string }>(),

    'Refresh capacity graph': emptyProps(),
    'No last capacity graph request data found': emptyProps(),

    'Get ntc planned outage': props<{ id: string }>(),
    'Get ntc planned outage success': props<{ plannedOutage: PlannedOutageDetailsStoreModel }>(),
    'Get ntc planned outage fail': props<{ errorMsg: string }>(),

    'Get ntc unplanned outage': props<{ id: string }>(),
    'Get ntc unplanned outage success': props<{ unplannedOutage: UnplannedOutageDetailsStoreModel }>(),
    'Get ntc unplanned outage fail': props<{ errorMsg: string }>(),

    'Delete ntc planned outage': props<{ id: string; refresh: boolean }>(),
    'Delete ntc planned outage success': props<{ id: string; refresh: boolean }>(),
    'Delete ntc planned outage fail': props<{ errorMsg: string }>(),

    'Cancel ntc planned outage': props<{ id: string }>(),
    'Cancel ntc planned outage success': emptyProps(),
    'Cancel ntc planned outage fail': props<{ errorMsg: string }>(),

    'Cancel ntc unplanned outage': props<{ id: string }>(),
    'Cancel ntc unplanned outage success': emptyProps(),
    'Cancel ntc unplanned outage fail': props<{ errorCode: string; errorMsg: string }>(),

    'Update ntc planned outage': props<{ id: string; request: UpdatePlannedOutageRequestStoreModel }>(),
    'Update ntc planned outage success': emptyProps(),
    'Update ntc planned outage fail': props<{ errorMsg: string }>(),

    'Update ntc unplanned outage': props<{ id: string; request: UpdateUnplannedOutageRequestStoreModel; connectedToCrisis?: boolean }>(),
    'Update ntc unplanned outage success': props<{ connectedToCrisis?: boolean }>(),
    'Update ntc unplanned outage fail': props<{ errorMsg: string }>(),

    'Get ntc planned outages': props<{ action: CapacityNtcGetPlannedOutagesStoreModel }>(),
    'Get ntc planned outages success': props<{ plannedOutages: PlannedOutageStoreModel[]; total: number }>(),
    'Get ntc planned outages fail': props<{ errorMsg: string }>(),

    'Refresh ntc planned outages': emptyProps(),

    'Get next ntc planned outages': emptyProps(),
    'Get next ntc planned outages success': props<{ plannedOutages: PlannedOutageStoreModel[]; total: number; offset: number }>(),
    'Get next ntc planned outages fail': props<{ errorMsg: string }>(),

    'Get ntc unplanned outages': props<{ action: CapacityNtcGetUnplannedOutagesStoreModel }>(),
    'Get ntc unplanned outages success': props<{ unplannedOutages: UnplannedOutageStoreModel[]; total: number }>(),
    'Get ntc unplanned outages fail': props<{ errorMsg: string }>(),

    'Get next ntc unplanned outages': emptyProps(),
    'Get next ntc unplanned outages success': props<{ unplannedOutages: UnplannedOutageStoreModel[]; total: number; offset: number }>(),
    'Get next ntc unplanned outages fail': props<{ errorMsg: string }>(),

    'Get ntc planned outage options': emptyProps(),
    'Get ntc planned outage options success': props<{ plannedOutageOptions: PlannedOutageOptionsStoreModel }>(),
    'Get ntc planned outage options fail': props<{ errorMsg: string }>(),

    'Get ntc unplanned outage options': emptyProps(),
    'Get ntc unplanned outage options success': props<{ unplannedOutageOptions: UnplannedOutageOptionsStoreModel }>(),
    'Get ntc unplanned outage options fail': props<{ errorMsg: string }>(),

    'Create ntc planned outage': props<{ request: CreatePlannedOutageRequestStoreModel }>(),
    'Create ntc planned outage success': emptyProps(),
    'Create ntc planned outage fail': props<{ errorMsg: string }>(),

    'Create ntc unplanned outage': props<{ request: CreateUnplannedOutageRequestStoreModel }>(),
    'Create ntc unplanned outage success': emptyProps(),
    'Create ntc unplanned outage fail': props<{ errorMsg: string }>(),

    'Get ntc options': emptyProps(),
    'Get ntc options success': props<{ getNtcOptionsResponse: GetNtcOptionsResponseStoreModel }>(),
    'Get ntc options fail': props<{ errorMsg: string }>(),

    'Get ntc': props<{ request: CapacityGetNtcRequestStoreModel }>(),
    'Get ntc success': props<{ getNtcResponse: GetNtcResponseStoreModel }>(),
    'Get ntc fail': props<{ errorMsg: string }>(),

    'Submit ntc': props<{
      request: CapacitySubmitNtcRequestStoreModel;
      reloadListOnSuccess: boolean;
    }>(),
    'Submit ntc success': props<{
      request: CapacitySubmitNtcRequestStoreModel;
      reloadListOnSuccess: boolean;
    }>(),
    'Submit ntc fail': props<{ errorMsg: string }>(),

    'Get atc': props<{ request: CapacityGetAtcRequestStoreModel }>(),
    'Get atc success': props<{ getAtcResponse: GetAtcResponseStoreModel }>(),
    'Get atc fail': props<{ errorMsg: string }>(),

    'Submit atc': props<{ request: CapacitySubmitAtcRequestStoreModel }>(),
    'Submit atc success': props<{ request: CapacitySubmitAtcRequestStoreModel }>(),
    'Submit atc fail': props<{ errorMsg: string }>(),

    'Get atc overview': props<{ request: CapacityGetAtcOverviewStoreModel; refresh?: boolean }>(),
    'Get atc overview success': props<{ overview: AtcOverviewDataStoreModel }>(),
    'Get atc overview fail': props<{ errorMsg: string }>(),

    'Refresh atc overview': emptyProps(),
    'No last atc overview request data found': emptyProps(),

    'Clear ntc overview': emptyProps(),
    'Clear capacity overview': emptyProps(),
    'Clear ntc planned outage options': emptyProps(),
    'Clear ntc planned outage': emptyProps(),
    'Clear ntc planned outages': emptyProps(),
    'Clear ntc unplanned outage options': emptyProps(),
    'Clear ntc unplanned outage': emptyProps(),
    'Clear ntc unplanned outages': emptyProps(),
    'Clear ntc': emptyProps(),
    'Clear ntc mtus': emptyProps(),
    'Clear atc': emptyProps(),
    'Clear atc overview': emptyProps(),
  },
});
